import { useState } from 'react';
import { Filter, FilterWrite, SetFilterValueOptions } from './filter-types';
import { useFilterClears } from './use-filter-clears';

export const useFilterStateMode = ({ filters }: { filters: Filter[] }) => {
  const preSelectedFilters = filters
    .filter((f) => f.preSelected || f.permanent)
    .map((f) => f.id);
  const [visibleFilters, setFiltersVisible] =
    useState<string[]>(preSelectedFilters);
  const [filterValues, setFilterValues] = useState<{
    [key: string]: unknown;
  }>({});
  const { clearCounts, noteWrite } = useFilterClears();

  const setFilterValue = (
    id: string,
    value: string | string[] | null | undefined,
    filter?: Filter,
    // Only the URL has history to replace, so state mode ignores this.
    _options?: SetFilterValueOptions,
  ) => {
    noteWrite(id, value);
    setFilterValues((previousFilterValues) => {
      const newFilterState = { ...previousFilterValues };
      // A range's value is `[lower, upper]`; an empty bound is unset.
      if (filter?.type === 'range') {
        const [lower, upper] = Array.isArray(value) ? value : [];
        delete newFilterState[filter.minKey];
        delete newFilterState[filter.maxKey];
        if (lower) {
          newFilterState[filter.minKey] = lower;
        }
        if (upper) {
          newFilterState[filter.maxKey] = upper;
        }
        return newFilterState;
      }
      if (filter?.type === 'dateRange' && !value) {
        delete newFilterState[filter.startKey];
        delete newFilterState[filter.endKey];
      } else {
        delete newFilterState[id];
      }

      if (!value) {
        delete newFilterState[id];
      } else {
        if (!Array.isArray(value)) {
          newFilterState[id] = String(value);
        } else {
          newFilterState[id] = value;
        }
      }
      return newFilterState;
    });
  };

  // State mode has no history, so the writes are applied one after another.
  const applyFilterWrites = (
    writes: FilterWrite[],
    _options?: SetFilterValueOptions,
  ) => {
    writes.forEach(({ id, value, filter }) =>
      setFilterValue(id, value, filter),
    );
  };

  const setVisibleFilters = (value: string[]) => {
    if (value.length < 1) {
      filters.map((filter) => {
        setFilterValue(filter.id, undefined, filter);
      });
      setFiltersVisible([]);
    } else {
      setFiltersVisible((oldFilters) => {
        const removedFilters = oldFilters.filter(
          (filter) => !value.includes(filter),
        );
        removedFilters.map((filterId) => {
          setFilterValue(
            filterId,
            undefined,
            filters.find((filter) => filter.id === filterId),
          );
        });
        return value;
      });
    }
  };

  const removeFilter = (filter?: Filter) => {
    setFiltersVisible((filters) => {
      const index = filters.findIndex((x) => x === filter?.id);
      if (index >= 0) {
        const removedFilters = filters.splice(index, 1);
        removedFilters.map((value) => {
          setFilterValue(value, undefined, filter);
        });
      }
      return [...filters];
    });
  };

  return {
    setFilterValue,
    applyFilterWrites,
    setFilterValues,
    setVisibleFilters,
    visibleFilters,
    filterValues,
    clearCounts,
    removeFilter,
  };
};
